import { useSelector } from 'react-redux';
import { Box, IconButton, Tooltip, Text } from '@chakra-ui/react';
import StatusCellRenderer from 'components/v4/StatusCellRenderer';
import path from '@/routes/constants';
import { IconComponent } from './IconComponent';
import { Link } from 'react-router-dom';

import { DIRECT_ROUTING, statuses } from '../constants';
import { CALLING_DISABLED, getStatusPill } from './helpers/serviceStatusLabel';

// (3158) Every service card shows the same small pill: no dot, at least
// 100px wide, whatever the card.
const compactPillSx = {
    display: 'inline-flex',
    '& > div': {
        minWidth: '100px',
        paddingLeft: '10px',
        paddingRight: '10px',
    },
};

const StatusPill = (props) => (
    <Box sx={compactPillSx}>
        <StatusCellRenderer {...props} noCircle isSmall fitText />
    </Box>
);

export const ServiceStatus = ({
    service,
    cardStatus,
    handleActions,
    isTrialExpired,
    isVendorCard = false,
    serviceObj,
}) => {
    const accountScope = useSelector((state) => state.login.requiredScope);
    const { darkMode } = useSelector((state) => state.settings);
    const { handleGrantAccess } = handleActions || {};
    const handleClick =
        cardStatus?.actionIconTooltip === 'Grant Access'
            ? handleGrantAccess
            : null;

    const handleTooltipIconClick = () => {
        if (cardStatus?.tooltipIcon === 'ExternalLink' && cardStatus?.link) {
            window.open(cardStatus.link, '_blank', 'noopener,noreferrer');
        }
    };

    const serviceId = service?.id || service?.serviceId || service?.serviceID;

    const canEdit = accountScope >= 40 && cardStatus?.showEdit;
    const editPath =
        service?.serviceType === DIRECT_ROUTING
            ? `${path.SERVICES_DIRECT_ROUTING}/${serviceId}/edit`
            : `${path.SERVICES_DIRECT_ROUTING_GCCHIGH}/${serviceId}/edit`;
    // The collapsed header's badge reads its tooltip from the same pill.
    const pill = getStatusPill(cardStatus, { isVendorCard, isTrialExpired });
    const statusPill = (
        <StatusPill
            value={pill.value}
            statuses={statuses(pill.label)}
            fitText={true}
        />
    );

    if (!service) return <Text>...</Text>;

    if (cardStatus?.tooltipIcon || isTrialExpired) {
        return (
            <Box
                sx={{
                    flexDirection: 'row',
                    display: 'flex',
                    justifyContent: 'flex-end',
                }}>
                {statusPill}

                <span className="mx-1 inline-flex flex-shrink-0 self-center">
                    <Tooltip
                        label={
                            isTrialExpired &&
                            cardStatus?.statusLabel !== CALLING_DISABLED
                                ? 'Your trial has expired and calling services may be disabled.  Please contact your provider. '
                                : cardStatus?.tooltip ||
                                  cardStatus?.statusDisplay
                                      ?.statusDisplayExtended
                        }
                        hasArrow
                        placement="bottom">
                        <IconButton
                            icon={
                                <IconComponent
                                    icon={
                                        isTrialExpired
                                            ? 'Warning'
                                            : cardStatus?.tooltipIcon
                                    }
                                    color={darkMode ? 'white' : 'black'}
                                    className={
                                        cardStatus?.tooltipIcon === 'Spinner'
                                            ? 'animate-spin'
                                            : ''
                                    }
                                />
                            }
                            size="xs"
                            bg="transparent"
                            _hover={{ bg: 'transparent' }}
                            onClick={handleTooltipIconClick}
                            cursor={
                                cardStatus?.tooltipIcon === 'ExternalLink' &&
                                cardStatus?.link
                                    ? 'pointer'
                                    : 'default'
                            }
                        />
                    </Tooltip>
                </span>

                {cardStatus?.actionIcon &&
                    !(cardStatus?.actionIcon === 'Edit' && canEdit) && (
                        <span className="my-auto inline-flex flex-shrink-0 self-center">
                            <Tooltip
                                label={cardStatus?.actionIconTooltip}
                                hasArrow
                                placement="bottom">
                                <IconButton
                                    icon={
                                        <IconComponent
                                            icon={cardStatus?.actionIcon}
                                            color={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    size="xs"
                                    bg="transparent"
                                    _hover={{ bg: 'transparent' }}
                                    _active={{ bg: 'transparent' }}
                                    _focus={{ bg: 'transparent' }}
                                    onClick={handleClick}
                                />
                            </Tooltip>
                        </span>
                    )}

                {cardStatus?.actionIcon &&
                    cardStatus?.actionIcon === 'Edit' &&
                    canEdit && (
                        <span className="my-auto inline-flex flex-shrink-0 self-center">
                            <Tooltip
                                label={cardStatus?.actionIconTooltip}
                                hasArrow
                                placement="bottom">
                                <Link
                                    to={editPath}
                                    state={serviceObj}
                                    className="hover:text-inherit hover:!no-underline">
                                    <IconButton
                                        icon={
                                            <IconComponent
                                                icon="Edit"
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        size="xs"
                                        bg="transparent"
                                        _hover={{
                                            bg: 'transparent',
                                        }}
                                        _focus={{
                                            bg: 'transparent',
                                        }}
                                        _active={{ bg: 'transparent' }}
                                    />
                                </Link>
                            </Tooltip>
                        </span>
                    )}
            </Box>
        );
    }

    return statusPill;
};

export default ServiceStatus;
